import cx from 'classnames';
import { isEmpty } from 'lodash';
import React from 'react';

import useThemeBlock from '@/hooks/useThemeBlock';
import { Section } from '@/helpers/Section';
import { LinkProps } from '@/helpers/Link/render';
import { ListEvents, ButtonSecondary } from '@/components/renders';
import { ListEventsProps } from '@/components/molecules/ListEvents/render';

import block from './block.json';
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
type SectionCardsEventProps = SectionOneColumnProps & {
	link?: LinkProps;
} & ListEventsProps;

interface ForwardedSectionCardsEvent
	extends React.ForwardRefExoticComponent<
			SectionCardsEventProps & React.RefAttributes<HTMLDivElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const SectionCardsEvent = React.forwardRef<
	HTMLDivElement,
	SectionCardsEventProps
>(
	(
		{
			anchor,
			className,
			title,
			link,
			events,
			slug,
			variant: currentVariant = 'default',
			titleTag = 'h2',
			theme = {},
		},
		ref
	) => {
		const { variant, blockTheme } = useThemeBlock(
			slug,
			theme,
			currentVariant
		);

		const trimmedSlug = slug.split('/');
		const modifierSlug = trimmedSlug[trimmedSlug.length - 1];

		const rootClass = cx(
			'supt-section supt-section--oneCol',
			`supt-section--${modifierSlug}`,
			'supt-section--colored',
			getStyles(events?.length),
			className
		);

		return isEmpty(events) ? null : (
			<Section
				id={anchor}
				className={rootClass}
				variant={variant}
				slug={slug}
				ref={ref}
			>
				<div className="supt-section__inner">
					{(title || link?.href) && (
						<div className="supt-section__headline">
							{title && (
								<Section.Title
									text={title}
									titleTag={titleTag}
								/>
							)}

							{link?.href ? (
								<div className="supt-section__linkWrapper">
									<ButtonSecondary {...link} />
								</div>
							) : null}
						</div>
					)}
					<div className="supt-section__content">
						<ListEvents events={events} />
					</div>
				</div>
			</Section>
		);
	}
) as ForwardedSectionCardsEvent;

SectionCardsEvent.slug = block.slug;
SectionCardsEvent.title = block.title;
